import { useEffect, useState } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANY, NAV_LINKS } from '../mock/data';

const JOBBER_REQUEST_URL =
  process.env.REACT_APP_JOBBER_REQUEST_FORM_URL ||
  '#quote';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top tagline strip */}
      <div className={`w-full bg-gold text-ink text-[11px] uppercase tracking-[0.3em] font-semibold text-center py-1.5 transition-[transform,opacity] ${scrolled ? '-translate-y-full opacity-0 h-0 py-0' : 'translate-y-0 opacity-100'}`}>
        Whatever They Can Do &mdash; We Can Do <span className="underline underline-offset-2">Better</span>
      </div>

      {/* Main navbar */}
      <div className={`transition-[background,box-shadow,padding] duration-300 ${scrolled ? 'bg-ink/95 backdrop-blur-md shadow-lg py-3 border-b border-white/5' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-11 h-11 rounded-md bg-gold flex items-center justify-center text-ink font-display text-2xl font-bold shadow-md group-hover:rotate-3 transition-transform">B</div>
            <div className="leading-tight">
              <div className="font-display text-cream text-xl uppercase tracking-wide">Better Fence &amp; Deck</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-gold -mt-0.5">Shreveport, Louisiana</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV_LINKS.filter(l => !['Home'].includes(l.label)).map((l) => (
              l.href.startsWith('/') ? (
                <Link
                  key={l.href}
                  to={l.href}
                  className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/80 hover:text-gold relative after:absolute after:left-0 after:-bottom-2 after:h-0.5 after:w-0 after:bg-gold after:transition-[width] hover:after:w-full transition-colors"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  className="text-xs uppercase tracking-[0.2em] font-semibold text-cream/80 hover:text-gold relative after:absolute after:left-0 after:-bottom-2 after:h-0.5 after:w-0 after:bg-gold after:transition-[width] hover:after:w-full transition-colors"
                >
                  {l.label}
                </a>
              )
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-cream hover:text-gold font-semibold text-sm transition-colors">
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
            <a
              href={JOBBER_REQUEST_URL}
              target={JOBBER_REQUEST_URL.startsWith('http') ? '_blank' : undefined}
              rel={JOBBER_REQUEST_URL.startsWith('http') ? 'noopener noreferrer' : undefined}
              data-testid="navbar-get-quote-btn"
              className="btn-primary"
            >
              Get a Quote
            </a>
          </div>

          <button aria-label="Menu" className="lg:hidden text-cream" onClick={() => setOpen(!open)}>
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-6 pt-2 bg-ink border-t border-white/5">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              l.href.startsWith('/') ? (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 px-2 text-cream border-b border-white/5 font-medium uppercase tracking-widest text-sm"
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 px-2 text-cream border-b border-white/5 font-medium uppercase tracking-widest text-sm"
                >
                  {l.label}
                </a>
              )
            ))}
          </nav>
          <div className="mt-4 flex flex-col gap-2">
            <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center justify-center gap-2 py-3 rounded-md border border-white/15 text-cream font-semibold">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <a
              href={JOBBER_REQUEST_URL}
              target={JOBBER_REQUEST_URL.startsWith('http') ? '_blank' : undefined}
              rel={JOBBER_REQUEST_URL.startsWith('http') ? 'noopener noreferrer' : undefined}
              onClick={() => setOpen(false)}
              data-testid="mobile-get-quote-btn"
              className="btn-primary text-center justify-center"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
